import React from "react";
import { FlaskConical, Palette } from "lucide-react";
import { Section, SectionHeading } from "@/components/layout/Section";
import { MicroLabel } from "@/components/common/MicroLabel";
import { Reveal } from "@/components/common/Reveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MATERIALS } from "@/content/homeContent";
import { MATERIAL_IMAGES } from "@/assets/materials";

const OnRequestCard = ({ icon: Icon, title, description, testId }) => (
  <div
    data-testid={testId}
    className="flex items-start gap-4 rounded-2xl border border-strato-gray-100 bg-strato-white p-5"
  >
    <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl border border-strato-gray-100 bg-strato-gray-50 text-strato-blue">
      <Icon className="h-5 w-5" strokeWidth={1.5} />
    </span>
    <div>
      <h3 className="font-display text-base font-medium text-strato-graphite">
        {title}
      </h3>
      <p className="mt-1 text-sm leading-[1.55] text-muted-foreground">
        {description}
      </p>
    </div>
  </div>
);

const SampleTile = ({ color, index }) => {
  const isWhite = color.name === "Pure White";
  return (
    <div
      data-testid={`material-swatch-${index}`}
      tabIndex={0}
      className="group rounded-2xl border border-strato-gray-100 bg-strato-white p-4 outline-none transition-colors duration-150 hover:border-strato-gray-300 focus-visible:ring-2 focus-visible:ring-[var(--strato-focus-ring)]"
    >
      <div
        className={
          "overflow-hidden rounded-xl " +
          (isWhite ? "border border-strato-gray-100 bg-strato-offwhite" : "bg-strato-offwhite")
        }
      >
        <img
          src={MATERIAL_IMAGES[color.name]}
          alt={`PLA ${color.name}`}
          loading="lazy"
          className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
      </div>
      <div className="mt-4 text-center">
        <p className="font-display text-[15px] font-medium text-strato-graphite">
          {color.name}
        </p>
        <div className="mt-1 flex items-center justify-center gap-2">
          <MicroLabel>PLA</MicroLabel>
          <span className="h-1 w-1 rounded-full bg-strato-gray-300" />
          <MicroLabel>{color.note}</MicroLabel>
        </div>
      </div>
    </div>
  );
};

export const Materials = () => {
  return (
    <Section id="materials-section" tone="offwhite">
      <Reveal>
        <SectionHeading
          microLabel={MATERIALS.microLabel}
          title={MATERIALS.title}
          description={MATERIALS.description}
        />
      </Reveal>

      <Reveal delay={0.06}>
        <div className="mt-10" data-testid="materials-tabs">
          <Tabs defaultValue="pla">
            <TabsList className="bg-strato-gray-50">
              <TabsTrigger value="pla" data-testid="materials-tab-pla">
                PLA standard
              </TabsTrigger>
              <TabsTrigger value="request" data-testid="materials-tab-request">
                Su richiesta
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pla" className="mt-8">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                {MATERIALS.colors.map((color, i) => (
                  <Reveal key={color.name} delay={i * 0.04}>
                    <SampleTile color={color} index={i} />
                  </Reveal>
                ))}
              </div>
              <p className="strato-micro mt-6 text-muted-foreground">
                RIFERIMENTI COLORE · TEXTURE FDM · LA RESA PUÒ VARIARE
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {MATERIALS.confirmNote}
              </p>
            </TabsContent>

            <TabsContent value="request" className="mt-8">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="materials-on-request">
                <OnRequestCard
                  testId="materials-custom-material"
                  icon={FlaskConical}
                  title="Altri materiali su richiesta"
                  description="Il PLA è il nostro materiale principale, ma non stampiamo solo PLA: altri materiali possono essere valutati su richiesta, in base al progetto. Indicalo nella richiesta di preventivo."
                />
                <OnRequestCard
                  testId="materials-custom-color"
                  icon={Palette}
                  title="Altri colori su richiesta"
                  description="Oltre ai cinque colori standard, sono disponibili colori personalizzati su richiesta, in base a disponibilità e progetto."
                />
              </div>
              <p className="strato-micro mt-6 text-muted-foreground">
                MATERIALI E COLORI PERSONALIZZATI · VALUTATI SUL PROGETTO
              </p>
            </TabsContent>
          </Tabs>
        </div>
      </Reveal>
    </Section>
  );
};
